import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
faInfoCircle,
faCheckCircle,
faExclamationTriangle,
faTimesCircle,
faQuestionCircle,
faExclamationCircle,
faCheck,
faTimes,
faSave,
faTrash,
faWarning,
faCircleInfo
} from '@fortawesome/free-solid-svg-icons';

import './universal-modal.css';

const UniversalModal = ({
isOpen = false,
onClose,
onConfirm,
type = 'info',
title,
message,
content,
input,
buttons = [],
showCloseButton = true,
closeOnBackdrop = true,
closeOnEscape = true,
animationDuration = 200
}) => {
const [isVisible, setIsVisible] = useState(false);
const [isClosing, setIsClosing] = useState(false);
const [inputValue, setInputValue] = useState(input?.defaultValue || '');
const modalRef = useRef(null);

// Иконки для разных типов модальных окон
const icons = {
info: faInfoCircle,
success: faCheckCircle,
warning: faExclamationTriangle,
error: faTimesCircle,
danger: faExclamationCircle,
question: faQuestionCircle,
confirm: faQuestionCircle
};

// Цвета кнопок по умолчанию для разных типов
const defaultButtons = {
info: [{ text: 'OK', type: 'primary' }],
success: [{ text: 'OK', type: 'success' }],
warning: [{ text: 'OK', type: 'warning' }],
error: [{ text: 'OK', type: 'danger' }],
question: [
{ text: 'Скасувати', type: 'outline' },
{ text: 'OK', type: 'primary' }
],
confirm: [
{ text: 'Скасувати', type: 'outline' },
{ text: 'Підтвердити', type: 'primary' }
]
};

// Заголовки по умолчанию
const defaultTitles = {
info: 'Інформація',
success: 'Успіх',
warning: 'Попередження',
error: 'Помилка',
danger: 'Увага',
question: 'Питання',
confirm: 'Підтвердження'
};

const currentButtons = buttons.length > 0 ? buttons : defaultButtons[type] || defaultButtons.info;
const currentTitle = title || defaultTitles[type] || defaultTitles.info;

useEffect(() => {
if (isOpen) {
setIsVisible(true);
setIsClosing(false);
setInputValue(input?.defaultValue || '');
} else {
startCloseAnimation();
}
}, [isOpen, input?.defaultValue]);

useEffect(() => {
if (closeOnEscape) {
const handleEscape = (e) => {
if (e.key === 'Escape' && isVisible) {
handleClose();
}
};
  document.addEventListener('keydown', handleEscape);
  return () => document.removeEventListener('keydown', handleEscape);
}

}, [isVisible, closeOnEscape]);

const startCloseAnimation = () => {
if (isVisible) {
setIsClosing(true);
setTimeout(() => {
setIsVisible(false);
setIsClosing(false);
}, animationDuration);
}
};

const handleClose = () => {
if (onClose) {
onClose();
}
startCloseAnimation();
};

const handleConfirm = (button) => {
if (onConfirm) {
const result = {
button: button.text,
type: button.type,
inputValue: input ? inputValue : undefined
};
onConfirm(result);
}
if (button.closeOnClick !== false) {
  handleClose();
}

};

const handleBackdropClick = (e) => {
if (closeOnBackdrop && e.target === e.currentTarget) {
handleClose();
}
};

const renderInput = () => {
if (!input) return null;
return (
  <div className="modal-input">
    <input
      type={input.type || 'text'}
      className="form-control"
      placeholder={input.placeholder}
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onKeyPress={(e) => {
        if (e.key === 'Enter' && input.submitOnEnter) {
          const primaryButton = currentButtons.find(btn => btn.type === 'primary') || currentButtons[currentButtons.length - 1];
          if (primaryButton) {
            handleConfirm(primaryButton);
          }
        }
      }}
      autoFocus={input.autoFocus}
    />
  </div>
);

};

const renderContent = () => {
if (!content) return null;
if (typeof content === 'string') {
  return (
    <div className="modal-content-body">
      {content}
    </div>
  );
}

if (Array.isArray(content)) {
  return (
    <ul className="modal-list">
      {content.map((item, index) => (
        <li key={index}>
          {item}
        </li>
      ))}
    </ul>
  );
}

return content;

};

if (!isVisible) return null;

return (
<div
className={`universal-modal ${type} ${isClosing ? 'fade-out' : ''}`}
style={{
position: 'fixed',
top: 0,
left: 0,
right: 0,
bottom: 0,
backgroundColor: 'rgba(0, 0, 0, 0.5)',
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
zIndex: 9999
}}
onClick={handleBackdropClick}
>
<div
className="modal-dialog"
ref={modalRef}
style={{
width: '100%',
maxWidth: '500px',
margin: '20px'
}}
>
<div className="modal-content">
{/* Header */}
<div className="modal-header">
<h5 className="modal-title">
<FontAwesomeIcon
icon={icons[type] || faCircleInfo}
className="modal-icon"
/>
{currentTitle}
</h5>
{showCloseButton && (
<button
type="button"
className="btn-close"
onClick={handleClose}
style={{
border: 'none',
background: 'none',
fontSize: '16px',
cursor: 'pointer',
opacity: 0.7
}}
>
</button>
)}
</div>
      {/* Body */}
      <div className="modal-body">
        {message && <p className="modal-message">{message}</p>}
        {renderContent()}
        {renderInput()}
      </div>

      {/* Footer */}
      <div className="modal-footer">
        {currentButtons.map((button, index) => (
          <button
            key={index}
            className={`modal-btn modal-btn-${button.type || 'primary'}`}
            onClick={() => handleConfirm(button)}
            disabled={button.disabled}
            autoFocus={button.autoFocus}
          >
            {button.icon && <FontAwesomeIcon icon={button.icon} />}
            {button.text}
          </button>
        ))}
      </div>
    </div>
  </div>
</div>

);
};

// Хук для удобного использования модального окна
export const useModal = () => {
const [modalProps, setModalProps] = useState({
isOpen: false,
type: 'info',
title: '',
message: '',
onConfirm: null,
onClose: null
});

const showModal = (props) => {
return new Promise((resolve) => {
setModalProps({
isOpen: true,
onConfirm: (result) => {
setModalProps(prev => ({ ...prev, isOpen: false }));
resolve(result);
},
onClose: () => {
setModalProps(prev => ({ ...prev, isOpen: false }));
resolve({ button: 'close', type: 'close' });
},
...props
});
});
};

const hideModal = () => {
setModalProps(prev => ({ ...prev, isOpen: false }));
};

// Методы для разных типов модальных окон
const alert = (message, title = 'Інформація') => {
return showModal({
type: 'info',
title,
message,
buttons: [{ text: 'OK', type: 'primary' }]
});
};

const confirm = (message, title = 'Підтвердження') => {
return showModal({
type: 'confirm',
title,
message,
buttons: [
{ text: 'Скасувати', type: 'outline' },
{ text: 'Підтвердити', type: 'primary' }
]
});
};

const prompt = (message, defaultValue = '', title = 'Введіть значення') => {
return showModal({
type: 'question',
title,
message,
input: {
defaultValue,
placeholder: 'Введіть текст...',
submitOnEnter: true,
autoFocus: true
},
buttons: [
{ text: 'Скасувати', type: 'outline' },
{ text: 'OK', type: 'primary' }
]
});
};

const success = (message, title = 'Успіх') => {
return showModal({
type: 'success',
title,
message,
buttons: [{ text: 'OK', type: 'success' }]
});
};

const error = (message, title = 'Помилка') => {
return showModal({
type: 'error',
title,
message,
buttons: [{ text: 'OK', type: 'danger' }]
});
};

const warning = (message, title = 'Попередження') => {
return showModal({
type: 'warning',
title,
message,
buttons: [{ text: 'OK', type: 'warning' }]
});
};

return {
modalProps,
showModal,
hideModal,
alert,
confirm,
prompt,
success,
error,
warning,
Modal: () => <UniversalModal {...modalProps} />
};
};

export default UniversalModal;